import React, { useCallback, useEffect, useState } from 'react';
import { PLAYS_API_URL } from './playsApi';
import './AdminAccounts.css';

// INTERNAL admin screen for the Plays billing model. Not linked from the
// visible nav; reached only by navigating to the 'admin' view explicitly.
//
// Backend contract (shopify-etl-upsellerate branch billing-plays, commit
// 2365db4, plays_routes.py). Also includes gmv_band ('up_to_3m' | '3m_to_6m'
// | 'over_6m' | null), gmv_computed_at and gmv_review (bool):
//   GET  /admin/accounts?limit=&offset=  header X-Admin-Token: <BILLING_ADMIN_TOKEN>
//        -> { accounts: [...], total, limit, offset }
//        account fields: account_id, plan, play_packs, founding,
//        founding_active, founding_started_at, founding_days_remaining,
//        one_time_used_this_cycle, one_time_total (null = unlimited),
//        always_on_active, always_on_total (null = unlimited),
//        over_capacity ({active, allowed} | null), cycle_reset_at,
//        subscription_status, billing_provider
//   POST /admin/founding {account_id, founding} same header
//        -> 409 when the 20-account founding cap is full
//
// The admin token is asked for once (password input), kept only in
// sessionStorage for the tab's lifetime, never in code or localStorage.
// Any 401 clears it and re-prompts.

const TOKEN_KEY = 'plays_admin_token';
const PAGE_SIZE = 50;

async function adminFetch(path, adminToken, { method = 'GET', body } = {}) {
  const response = await fetch(`${PLAYS_API_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Token': adminToken,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await response.json(); } catch { /* non-JSON body */ }
  if (!response.ok) {
    const err = new Error(data?.error || `Request failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  return data;
}

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toISOString().slice(0, 10);
};

const fmtLimit = (used, total) => `${used ?? 0} / ${total == null ? 'Unlimited' : total}`;

// gmv_band values per plays_model (commit 2365db4); null = not computed yet.
const GMV_BAND_LABELS = {
  up_to_3m: 'Up to $3M',
  '3m_to_6m': '$3M to $6M',
  over_6m: 'Over $6M',
};

const fmtGmvBand = (band) => GMV_BAND_LABELS[band] || 'Pending';

export default function AdminAccounts() {
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmFor, setConfirmFor] = useState(null); // account pending founding toggle
  const [busyAccountId, setBusyAccountId] = useState(null);

  const handleUnauthorized = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAdminToken('');
    setAccounts([]);
    setError('Invalid admin token. Enter it again.');
  }, []);

  const load = useCallback(async (token, pageOffset) => {
    setIsLoading(true);
    setError('');
    try {
      const data = await adminFetch(`/admin/accounts?limit=${PAGE_SIZE}&offset=${pageOffset}`, token);
      setAccounts(data?.accounts || []);
      setTotal(data?.total || 0);
      setOffset(pageOffset);
    } catch (err) {
      if (err.status === 401) {
        handleUnauthorized();
      } else {
        setError(err.message || 'Could not load accounts.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    if (adminToken) load(adminToken, 0);
  }, [adminToken, load]);

  const submitToken = (e) => {
    e.preventDefault();
    const value = tokenInput.trim();
    if (!value) return;
    sessionStorage.setItem(TOKEN_KEY, value);
    setTokenInput('');
    setError('');
    setAdminToken(value);
  };

  const toggleFounding = async (account) => {
    setBusyAccountId(account.account_id);
    setError('');
    try {
      await adminFetch('/admin/founding', adminToken, {
        method: 'POST',
        body: { account_id: account.account_id, founding: !account.founding },
      });
      setConfirmFor(null);
      await load(adminToken, offset);
    } catch (err) {
      if (err.status === 401) {
        handleUnauthorized();
      } else {
        setError(err.message || 'Could not update founding status.');
      }
      setConfirmFor(null);
    } finally {
      setBusyAccountId(null);
    }
  };

  if (!adminToken) {
    return (
      <div className="admin-accounts admin-accounts-login">
        <form className="admin-accounts-login-card" onSubmit={submitToken}>
          <h2>Admin access</h2>
          <p>Enter the billing admin token to manage accounts. It is kept only for this tab.</p>
          {error && <div className="admin-accounts-error">{error}</div>}
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
            autoFocus
          />
          <button type="submit" disabled={!tokenInput.trim()}>Continue</button>
        </form>
      </div>
    );
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="admin-accounts">
      <div className="admin-accounts-header">
        <div>
          <h2>Accounts</h2>
          <p>{total} account{total === 1 ? '' : 's'} · internal billing admin</p>
        </div>
        <div className="admin-accounts-header-actions">
          <button type="button" onClick={() => load(adminToken, offset)} disabled={isLoading}>
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            type="button"
            className="admin-accounts-logout"
            onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setAdminToken(''); }}
          >
            Forget token
          </button>
        </div>
      </div>

      {error && <div className="admin-accounts-error">{error}</div>}

      <div className="admin-accounts-table-wrap">
        <table className="admin-accounts-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Plan</th>
              <th>Packs</th>
              <th>Founding</th>
              <th>One-time</th>
              <th>Always-on</th>
              <th>GMV band</th>
              <th>Cycle resets</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => {
              const over = account.over_capacity;
              return (
                <tr key={account.account_id} className={over ? 'is-over-capacity' : ''}>
                  <td className="admin-accounts-id">{account.account_id}</td>
                  <td className="admin-accounts-plan">{account.plan}</td>
                  <td>{account.play_packs || 0}</td>
                  <td>
                    {account.founding ? (
                      <span className="admin-accounts-founding-badge">
                        Founding
                        {account.founding_days_remaining != null && (
                          <span className="admin-accounts-founding-days">
                            {account.founding_days_remaining}d left
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="admin-accounts-muted">-</span>
                    )}
                  </td>
                  <td>{fmtLimit(account.one_time_used_this_cycle, account.one_time_total)}</td>
                  <td>
                    {fmtLimit(account.always_on_active, account.always_on_total)}
                    {over && (
                      <span className="admin-accounts-over-badge" title={`Running ${over.active}, allowed ${over.allowed}`}>
                        over capacity
                      </span>
                    )}
                  </td>
                  <td>
                    {fmtGmvBand(account.gmv_band)}
                    {account.gmv_review && (
                      <span className="admin-accounts-gmv-review-badge" title="High GMV account checked out at list price; pricing review pending">
                        review
                      </span>
                    )}
                  </td>
                  <td>{fmtDate(account.cycle_reset_at)}</td>
                  <td>{account.subscription_status || '-'}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-accounts-toggle"
                      disabled={busyAccountId === account.account_id}
                      onClick={() => setConfirmFor(account)}
                    >
                      {account.founding ? 'Remove founding' : 'Make founding'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {accounts.length === 0 && !isLoading && (
              <tr>
                <td colSpan={10} className="admin-accounts-empty">No accounts found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="admin-accounts-pagination">
        <button
          type="button"
          disabled={offset === 0 || isLoading}
          onClick={() => load(adminToken, Math.max(0, offset - PAGE_SIZE))}
        >
          Previous
        </button>
        <span>Page {page} of {pages}</span>
        <button
          type="button"
          disabled={offset + PAGE_SIZE >= total || isLoading}
          onClick={() => load(adminToken, offset + PAGE_SIZE)}
        >
          Next
        </button>
      </div>

      {confirmFor && (
        <div className="admin-accounts-confirm-overlay" role="dialog" aria-modal="true">
          <div className="admin-accounts-confirm-card">
            <h4>{confirmFor.founding ? 'Remove founding access?' : 'Grant founding access?'}</h4>
            <p>
              {confirmFor.founding
                ? `${confirmFor.account_id} will lose founding entitlements.`
                : `${confirmFor.account_id} will get the Pro plan at $49 for 6 months. Max 20 founding accounts.`}
            </p>
            <div className="admin-accounts-confirm-actions">
              <button type="button" onClick={() => setConfirmFor(null)} disabled={busyAccountId != null}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-accounts-confirm-cta"
                onClick={() => toggleFounding(confirmFor)}
                disabled={busyAccountId != null}
              >
                {busyAccountId ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
